import { Button, Menu, Title } from "dash-styled"
import { FC, useCallback, useMemo, useState } from "react"
import {
    AITrainingInstance,
    AITrainingTemplate as AITrainingTemplateType,
    getAIInstanceID
} from "../../../../../../../../Models/API/AIModel"
import { Table, TableColumn } from "resha"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../../../Models/Client/ProjectRole"
import { metaAI } from "neql"
import { useNeqlContextProvider } from "../../../../../../../../Hooks/useNeqlContextProvider"
import { downloadDataAsZipFromURL } from "../../../../../../../../Helpers/Functions/downloadAs"
import { useDispatch } from "react-redux"
import { failMessage } from "../../../../../../../../Actions/Client/MessagesActions"

interface AITrainingTemplateProps {
    aiTrainingTemplate: AITrainingTemplateType
    testId?: string
    columns: TableColumn<AITrainingInstance>[]
    appRoles: ProjectRole[]
    appID: string
    onTrain: (aiTrainingTemplate: AITrainingTemplateType) => void
    onAIInstanceClick: (instance: AITrainingInstance) => void
}

export const AITrainingTemplate: FC<AITrainingTemplateProps> = ({
    aiTrainingTemplate,
    testId,
    columns,
    appRoles,
    appID,
    onTrain,
    onAIInstanceClick
}) => {
    const clientForApp = useNeqlContextProvider("metaAIApp", appID)
    const dispatch = useDispatch()
    const [isModelDownloading, setModelDownloading] = useState<boolean>(false)
    const [selectedInstanceId, setSelectedInstanceId] =
        useState<string | undefined>()

    const checkedInstanceIds = useMemo(
        () => (selectedInstanceId === undefined ? [] : [selectedInstanceId]),
        [selectedInstanceId]
    )

    const handleToggleInstance = useCallback((instance: AITrainingInstance) => {
        setSelectedInstanceId(prevId =>
            prevId === instance.id ? undefined : instance.id
        )
    }, [])

    const [requestModelId] = metaAI.useRequestMetaAiTrainingModelMutation({
        client: clientForApp
    })

    const dispatchDownloadFail = () => {
        dispatch(
            failMessage(
                "No data found to download. If you think this is a mistake, please contact developers@super.ai and we'll look into it."
            )
        )
    }

    const [downloadModel, { stopPolling }] =
        metaAI.useDownloadMetaAiTrainingModelLazyQuery({
            client: clientForApp,
            onCompleted: ({ download_artifact_async }) => {
                const onRequestEnd = () => {
                    stopPolling()
                    setModelDownloading(false)
                }

                if (download_artifact_async?.output) {
                    downloadDataAsZipFromURL(
                        download_artifact_async?.output.url
                    )
                    onRequestEnd()
                } else if (download_artifact_async?.errors) {
                    dispatchDownloadFail()
                    onRequestEnd()
                }
            }
        })

    const handleDownloadModel = async (type: string) => {
        if (selectedInstanceId) {
            const instance = aiTrainingTemplate.training_instances.find(
                ({ id }) => id === selectedInstanceId
            )
            if (instance) {
                setModelDownloading(true)
                const { data: requestModelResponse } = await requestModelId({
                    variables: {
                        artifactType: type,
                        modelId: instance?.modelId
                    }
                })

                if (requestModelResponse?.download_artifact_async) {
                    const { startPolling } = await downloadModel({
                        variables: {
                            id: requestModelResponse?.download_artifact_async
                        }
                    })
                    startPolling(5000)
                } else {
                    dispatchDownloadFail()
                    setModelDownloading(false)
                }
            }
        }
    }

    return (
        <section
            data-testid={testId}
            className="flex flex-col items-stretch xl:items-start py-5"
        >
            <div className="flex w-full items-center justify-between mb-2">
                <div>
                    <Title
                        variant="heading4"
                        component="h3"
                        weight="semibold"
                        className="mb-4 xl:mb-0 xl:mr-8"
                        noMargin={true}
                    >
                        {aiTrainingTemplate.name}
                    </Title>
                    {aiTrainingTemplate.root_model ? (
                        <div>Model: {aiTrainingTemplate.root_model.name}</div>
                    ) : null}
                </div>
                <div className="flex">
                    {selectedInstanceId ? (
                        <Menu
                            testId="download-model"
                            className="font-normal"
                            title="Download"
                            align="right"
                            nonInteractive={false}
                            onChoose={handleDownloadModel}
                            disabled={isModelDownloading}
                            items={[
                                {
                                    label: "Weights",
                                    value: "weights"
                                },
                                {
                                    label: "Source",
                                    value: "source"
                                }
                            ]}
                        />
                    ) : null}
                    <Button
                        className={`w-20 p-2 ml-4 ${
                            hasAppPermission(appRoles, "ai-training:train")
                                ? ""
                                : "hidden"
                        }`}
                        onClick={() => onTrain(aiTrainingTemplate)}
                    >
                        Train
                    </Button>
                </div>
            </div>
            {aiTrainingTemplate.training_instances.length > 0 && (
                <Table
                    className="w-full table-fixed"
                    itemCount={aiTrainingTemplate.training_instances.length}
                    columns={columns}
                    items={aiTrainingTemplate.training_instances}
                    getKey={getAIInstanceID}
                    onRowClick={onAIInstanceClick}
                    checkedItemIds={checkedInstanceIds}
                    onToggleRowCheck={handleToggleInstance}
                />
            )}
        </section>
    )
}
